import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import { TableHeaderWithTooltipIcon, TextButtonCell } from '~/components/common';
import { ReceiveSupplierCell, ReceiveSupplierHeader } from '~/components/receive';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import {
  RECEIVE_REPORT_STATUS,
  RECEIVE_REPORT_STATUS_BADGE_COLOR,
  RECEIVE_STATUS_STATUS,
  RECEIVE_STATUS_STATUS_BADGE_COLOR,
  RECEIVE_TOOLTIP_MESSAGE,
} from '~/constants';

import { formatReceiveExpectedDateTime } from '../formatReceiveExpectedDateTime';

import type { TypeReceiveStatusRow } from '~/types/receive.types';

/**
 * [입고관리 > 입고현황] 페이지 테이블
 */
const getReceiveStatusColumnDefs = ({
  mode = 'DEFAULT',
  onClickViewDetail,
}: {
  mode?: 'DEFAULT' | '3PL';
  onClickViewDetail: (data: TypeReceiveStatusRow) => void;
}) => {
  const columnDefs: ColDef<TypeReceiveStatusRow>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 72,
    },
    {
      field: 'requestCode',
      headerName: '입고요청번호',
      width: 180,
    },
    {
      field: 'status',
      headerName: '요청상태',
      width: 100,
      minWidth: 100,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: Badge,
      cellRendererParams: ({ value }: ICellRendererParams<TypeReceiveStatusRow, TypeReceiveStatusRow['status']>) => ({
        variant: 'light',
        status: true,
        color: value ? RECEIVE_STATUS_STATUS_BADGE_COLOR[value] : undefined,
        children: value ? RECEIVE_STATUS_STATUS[value] : '-',
      }),
    },
    {
      field: 'reportStatus',
      hide: mode !== '3PL',
      headerName: '리포트 상태',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: RECEIVE_TOOLTIP_MESSAGE.REPORT_STATUS_DESCRIPTION,
        tooltipMaxWidth: '258px',
      },
      width: 130,
      minWidth: 130,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<TypeReceiveStatusRow, TypeReceiveStatusRow['reportStatus']>) => ({
        variant: 'light',
        status: true,
        color: value ? RECEIVE_REPORT_STATUS_BADGE_COLOR[value] : undefined,
        children: value ? RECEIVE_REPORT_STATUS[value].text : '-',
      }),
    },
    {
      field: 'estimatedArrivalDate',
      headerName: '입고예정일시',
      width: 200,
      valueFormatter: ({
        data,
      }: ValueFormatterParams<TypeReceiveStatusRow, TypeReceiveStatusRow['estimatedArrivalDate']>) => {
        if (!data) {
          return '-';
        }
        return formatReceiveExpectedDateTime(data.estimatedArrivalDate, data.estimatedArrivalTime);
      },
    },
    {
      field: 'releaseCodeName',
      headerName: '입고 방법',
      width: 80,
    },
    {
      headerName: '',
      width: 80,
      cellRenderer: TextButtonCell,
      cellRendererParams: {
        children: '상세보기',
        showDashIfNoValue: false,
        onClick: ({ data }: ICellRendererParams<TypeReceiveStatusRow>) => {
          if (!data) return;
          onClickViewDetail(data);
        },
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerComponent: ReceiveSupplierHeader,
      cellRenderer: ReceiveSupplierCell<TypeReceiveStatusRow>,
      minWidth: 280,
      flex: 1,
    },
    {
      field: 'skuCnt',
      headerName: '요청 품목',
      width: 80,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveStatusRow, TypeReceiveStatusRow['skuCnt']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'qty',
      headerName: '요청 수량',
      width: 80,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveStatusRow, TypeReceiveStatusRow['qty']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'receivedSkuCnt',
      headerName: '입고 품목',
      width: 80,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveStatusRow, TypeReceiveStatusRow['receivedSkuCnt']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'receivedQty',
      headerName: '입고 수량',
      width: 80,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveStatusRow, TypeReceiveStatusRow['receivedQty']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'receivedDate',
      headerName: '입고확정일',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeReceiveStatusRow, TypeReceiveStatusRow['receivedDate']>) =>
        formatDate(value, 'YYYY.MM.DD') ?? '-',
    },
  ];

  return { columnDefs };
};

export default getReceiveStatusColumnDefs;
